/**
 * StudioLanding — public-facing entry experience for /studio.
 *
 * Premium hospitality creator landing — designed for Instagram bios, QR
 * codes, LinkedIn traffic, reels. NOT a login screen. The "Start Free"
 * CTA routes into the OTP login flow which auto-provisions a workspace.
 */
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ChefHat, Calculator, DollarSign, Calendar, Sparkles, Layers, Scale,
  ArrowRight, Star, Share2, Smartphone, Zap, Quote,
} from 'lucide-react';

const STUDIO_ROLE = 'studio_chef';

function FeatureCard({ Icon, title, description, accent, testId }) {
  return (
    <div data-testid={testId}
      className="relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-sm border border-[#F4ECE0] p-5 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all">
      <div className={`absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-30 blur-2xl ${accent}`} />
      <div className="relative">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md mb-3 ${accent}`}>
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <h3 className="text-base font-bold text-[#3A1F12] mb-1.5">{title}</h3>
        <p className="text-sm text-[#7A5A4B] leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export default function StudioLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Studio users already logged in skip the marketing page
    if (user?.role === STUDIO_ROLE) navigate('/studio/home', { replace: true });
  }, [user, navigate]);

  return (
    <div data-testid="studio-landing-page" className="min-h-screen relative overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #FFE7CD 0%, transparent 35%), ' +
          'radial-gradient(circle at 100% 30%, #F4DCC8 0%, transparent 40%), ' +
          'linear-gradient(180deg, #FFFBF5 0%, #FAF3EA 100%)',
      }}>
      {/* Top nav (lightweight — public, no app chrome) */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FFFBF5]/80 border-b border-[#F4ECE0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/studio" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E8643E] to-[#F4B860] flex items-center justify-center shadow-md">
              <ChefHat className="w-4 h-4 text-white" strokeWidth={2.25} />
            </div>
            <div className="leading-none">
              <p className="text-[15px] font-bold text-[#3A1F12] tracking-tight">Chef Studio</p>
              <p className="text-[10px] text-[#A0846B] font-medium">by RestroPulse</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/studio/login"
              data-testid="studio-landing-login-link"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[12px] font-semibold text-[#3A1F12] hover:bg-[#F4ECE0]/60">
              Login
            </Link>
            <Link to="/studio/login"
              data-testid="studio-landing-cta-start"
              className="inline-flex items-center gap-1 h-8 px-4 rounded-full bg-gradient-to-r from-[#E8643E] to-[#D44E2A] text-white text-[12px] font-semibold shadow-md hover:shadow-lg hover:from-[#D44E2A] transition-all">
              Start Free <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section data-testid="studio-landing-hero" className="relative">
        <div className="absolute right-0 top-0 w-96 h-96 rounded-full opacity-20 blur-3xl bg-[#E8643E] pointer-events-none" />
        <div className="absolute left-0 bottom-0 w-72 h-72 rounded-full opacity-20 blur-3xl bg-[#F4B860] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm border border-[#F4ECE0] mb-5">
            <Sparkles className="w-3 h-3 text-[#E8643E]" />
            <span className="text-[10px] uppercase tracking-wider text-[#7A2A12] font-semibold">
              Built for chefs, restaurateurs & culinary creators
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#3A1F12] tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Build smarter recipes.{' '}
            <span className="bg-gradient-to-r from-[#E8643E] via-[#D44E2A] to-[#B73C1B] bg-clip-text text-transparent">Cost better.</span>{' '}
            Operate profitably.
          </h1>
          <p className="text-base sm:text-lg text-[#5C4A3D] mt-5 max-w-2xl mx-auto leading-relaxed">
            Recipe Costing &middot; Pricing &middot; Production Planning &middot; Yield Calculations &middot; Sub-Recipes — all in one creator-first workspace.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/studio/login"
              data-testid="studio-hero-cta-start-free"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-[#E8643E] to-[#D44E2A] hover:from-[#D44E2A] hover:to-[#B73C1B] text-white font-semibold text-sm shadow-lg hover:shadow-2xl transition-all">
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features"
              data-testid="studio-hero-cta-explore"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/70 backdrop-blur-sm border border-[#F4ECE0] text-[#3A1F12] font-semibold text-sm hover:bg-white shadow-sm hover:shadow-md transition-all">
              Explore Features
            </a>
            <Link to="/studio/login"
              data-testid="studio-hero-cta-login"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-transparent border border-[#3A1F12]/15 text-[#3A1F12] font-semibold text-sm hover:bg-[#3A1F12]/5 transition-all">
              Login
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-7 text-[11px] text-[#7A5A4B]">
            <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3 h-3" /> Mobile OTP login</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> Setup in 30 seconds</span>
            <span className="inline-flex items-center gap-1.5"><Star className="w-3 h-3" /> No card · No setup fees</span>
          </div>
        </div>
      </section>

      {/* MOCK PREVIEW SURFACE — visual storytelling without real screenshot dependency */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 -mt-2 mb-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#3A1F12] to-[#5C2E1A] p-6 sm:p-10 shadow-2xl border border-[#3A1F12]/20 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-96 h-96 rounded-full opacity-30 blur-3xl bg-[#E8643E]" />
          <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-white/95 p-4 shadow-md">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Chicken Biryani</p>
              <p className="text-2xl font-extrabold text-[#3A1F12] tabular-nums mt-1">₹48</p>
              <p className="text-[11px] text-[#7A5A4B]">cost / portion · 200g plate</p>
              <div className="mt-2 h-1 rounded-full bg-[#F4ECE0] overflow-hidden">
                <div className="h-full w-[28%] bg-[#E8643E]" />
              </div>
              <p className="text-[10px] text-[#7A5A4B] mt-1">28% theo · ₹169 menu price</p>
            </div>
            <div className="rounded-xl bg-white/95 p-4 shadow-md">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Output</p>
              <p className="text-2xl font-extrabold text-[#3A1F12] tabular-nums mt-1">5kg → 25</p>
              <p className="text-[11px] text-[#7A5A4B]">portions auto-calculated</p>
              <div className="mt-2 grid grid-cols-3 gap-1 text-[9px] text-center">
                <div className="bg-[#FCE7D6] py-1 rounded text-[#7A2A12] font-semibold">Premium 20</div>
                <div className="bg-[#F4B860]/30 py-1 rounded text-[#7A2A12] font-semibold">Standard 25</div>
                <div className="bg-[#F4ECE0] py-1 rounded text-[#7A5A4B] font-semibold">Buffet 30</div>
              </div>
            </div>
            <div className="rounded-xl bg-white/95 p-4 shadow-md">
              <p className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Suggested pricing</p>
              <p className="text-2xl font-extrabold text-[#3A1F12] tabular-nums mt-1">₹169</p>
              <p className="text-[11px] text-[#7A5A4B]">menu · ₹217 swiggy · ₹225 zomato</p>
              <div className="mt-2 flex items-center gap-1">
                <span className="text-[9px] uppercase tracking-wider text-[#2D6A4F] bg-[#EAF4EE] px-2 py-0.5 rounded font-semibold">28% theo</span>
                <span className="text-[9px] uppercase tracking-wider text-[#5C6B64] bg-[#F3F5F2] px-2 py-0.5 rounded font-semibold">+commissions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0846B] font-semibold">Everything chefs actually need</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#3A1F12] mt-2 tracking-tight">
            Operational tools, creator-first.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard testId="studio-feature-recipe-costing"
            Icon={ChefHat} title="Recipe Costing"
            description="Multi-level sub-recipes, automatic cost roll-up, and per-portion math out of the box."
            accent="bg-gradient-to-br from-[#E8643E] to-[#D44E2A]" />
          <FeatureCard testId="studio-feature-sub-recipes"
            Icon={Layers} title="Sub-Recipes"
            description="Reuse marinades, sauces, doughs across menu items — change once, update everywhere."
            accent="bg-gradient-to-br from-[#A88B70] to-[#7A6650]" />
          <FeatureCard testId="studio-feature-pricing"
            Icon={DollarSign} title="Pricing Studio"
            description="Recipe-level Theo %, suggested menu price, Swiggy/Zomato uplift — zero spreadsheets."
            accent="bg-gradient-to-br from-[#F4B860] to-[#E8943C]" />
          <FeatureCard testId="studio-feature-production"
            Icon={Calendar} title="Production Planning"
            description="Plan service, build ingredient picklists, prep-now / activate-next flows. Built for ops."
            accent="bg-gradient-to-br from-[#7A9D6E] to-[#5C7E52]" />
          <FeatureCard testId="studio-feature-portion-cost"
            Icon={Calculator} title="Portion Costing"
            description="Define how many plates a 5kg batch yields — Studio auto-derives portion size and ₹/plate."
            accent="bg-gradient-to-br from-[#C68B65] to-[#A06D49]" />
          <FeatureCard testId="studio-feature-yield"
            Icon={Scale} title="Yield Calculations"
            description="Account for trim, shrinkage, and processing yield at every ingredient level."
            accent="bg-gradient-to-br from-[#5C7E52] to-[#3D5C36]" />
        </div>
      </section>

      {/* SOCIAL / SHAREABILITY */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#FFE7CD] to-[#F4DCC8] p-6 sm:p-10 border border-[#F4ECE0]">
          <div className="flex flex-col lg:flex-row items-center gap-6 justify-between">
            <div className="text-center lg:text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0846B] font-semibold mb-2">Built to share</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#3A1F12] tracking-tight max-w-md">
                Your craft deserves a beautiful canvas.
              </h3>
              <p className="text-sm text-[#5C4A3D] mt-2 max-w-md">
                Studio is designed so screenshots, reels, and stories look premium right out of the box. Public recipe sharing &amp; community discovery roll out next.
              </p>
            </div>
            <div className="flex flex-col gap-2.5 w-full max-w-xs">
              <button type="button"
                data-testid="studio-share-copy-link"
                onClick={() => { try { navigator.clipboard?.writeText(window.location.origin + '/studio'); } catch (e) { /* clipboard API unavailable in this context — silent */ } }}
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white text-[#3A1F12] font-semibold text-sm shadow-md hover:shadow-lg transition-all">
                <Share2 className="w-4 h-4" /> Copy Studio link
              </button>
              <Link to="/studio/library"
                data-testid="studio-share-explore"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#3A1F12] text-white font-semibold text-sm shadow-md hover:bg-[#5C2E1A] transition-all">
                Explore Community Recipes
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL/QUOTE — adds gravitas */}
      <section className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center">
          <Quote className="w-7 h-7 text-[#E8643E] mx-auto mb-3 opacity-60" />
          <p className="text-lg sm:text-xl text-[#3A1F12] italic font-medium leading-relaxed">
            "Recipe costing used to take half my week. With Studio, it's a five-minute job before service every day."
          </p>
          <p className="text-[11px] text-[#A0846B] mt-3 uppercase tracking-wider font-semibold">— Chef-led restaurants, Mumbai</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#3A1F12] to-[#5C2E1A] p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute right-1/4 top-0 w-72 h-72 rounded-full opacity-25 blur-3xl bg-[#E8643E]" />
          <div className="absolute left-1/4 bottom-0 w-64 h-64 rounded-full opacity-15 blur-3xl bg-[#F4B860]" />
          <div className="relative">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight max-w-2xl mx-auto">
              Ready to build your first recipe?
            </h3>
            <p className="text-sm sm:text-base text-[#FFE7CD]/80 mt-2 max-w-xl mx-auto">
              Mobile OTP login. Workspace ready instantly. No setup, no spreadsheets.
            </p>
            <Link to="/studio/login"
              data-testid="studio-final-cta"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-gradient-to-r from-[#E8643E] to-[#D44E2A] hover:from-[#D44E2A] text-white font-bold text-sm shadow-lg hover:shadow-2xl mt-6 transition-all">
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 text-center text-[10px] text-[#A0846B]">
        Chef Studio · powered by RestroPulse · Phase 1 · Mobile OTP login · No card required
      </footer>
    </div>
  );
}
